/**
 * Storefront brand palette (PD-16045).
 *
 * A portal site's brand supplies two things: a primary (the light-mode header
 * and bottom nav, and the pre-login welcome in both modes) and an accent, with
 * a light-mode and a dark-mode value (buttons, links, the active tab, the
 * focus ring). Everything else — page and card surfaces, text, borders,
 * inputs — is a fixed Workd neutral per mode, and in dark mode the header and
 * nav are that neutral too, so the brand never tints a background.
 *
 * The helpers here are isomorphic on purpose: the storefront applier, the
 * Portal Sites editor's preview and contrast guard, and the API's validation
 * all derive the same values from the same inputs.
 */

/** Fixed neutral surfaces per mode, the same values the CRM uses. */
export interface StorefrontNeutralsModel {
	/** Body / scroll surface behind cards. */
	page: string;
	/** A step above the page: filter chrome, subdued banners. */
	pageSoft: string;
	/** Cards, modals, drawers. */
	surface: string;
	/** Header and bottom nav in dark mode (light mode paints the primary). */
	header: string;
	border: string;
	input: string;
	text: string;
	textMuted: string;
}

export const STOREFRONT_NEUTRALS: Readonly<
	Record<'light' | 'dark', StorefrontNeutralsModel>
> = {
	light: {
		page: '#F9FAFB',
		pageSoft: '#F3F4F6',
		surface: '#FFFFFF',
		header: '#FFFFFF',
		border: '#E5E7EB',
		input: '#F9FAFB',
		text: '#111827',
		textMuted: '#6B7280',
	},
	dark: {
		page: '#111827',
		pageSoft: '#161E2B',
		surface: '#1F2937',
		header: '#1F2937',
		border: '#374151',
		input: '#111827',
		text: '#F3F4F6',
		textMuted: '#9CA3AF',
	},
};

/** The palette an unbranded portal site gets: the same Workd blue as the CRM. */
export const STANDARD_BRAND_PALETTE = {
	primaryColor: '#0058DB',
	accentColorLight: '#005EEB',
	accentColorDark: '#3B82F6',
} as const;

/** WCAG AA for normal text; what the editor and the API enforce. */
export const BRAND_MIN_CONTRAST = 4.5;

/** Near-black rather than pure black so text reads as ink on a bright color. */
export const BRAND_DARK_TEXT = '#0F172A';
export const BRAND_LIGHT_TEXT = '#FFFFFF';

/** Parse a 3- or 6-digit hex color into [r, g, b]; null when unparseable. */
export function parseHexColor(
	hex: string | null | undefined,
): [number, number, number] | null {
	if (hex == null) {
		return null;
	}
	const trimmed = hex.trim().replace(/^#/, '');
	const expanded =
		trimmed.length === 3
			? trimmed
					.split('')
					.map((c) => c + c)
					.join('')
			: trimmed;
	if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
		return null;
	}
	return [
		parseInt(expanded.slice(0, 2), 16),
		parseInt(expanded.slice(2, 4), 16),
		parseInt(expanded.slice(4, 6), 16),
	];
}

/** True for a value the palette accepts: a 3- or 6-digit hex with or without '#'. */
export function isHexColor(value: string | null | undefined): boolean {
	return parseHexColor(value) != null;
}

/** Canonical form: upper-case six-digit hex with a leading '#'. */
export function normalizeHexColor(hex: string): string | null {
	const rgb = parseHexColor(hex);
	if (rgb == null) {
		return null;
	}
	return rgbToHex(rgb);
}

function rgbToHex([r, g, b]: [number, number, number]): string {
	return `#${[r, g, b]
		.map((v) =>
			Math.round(Math.min(255, Math.max(0, v)))
				.toString(16)
				.padStart(2, '0'),
		)
		.join('')
		.toUpperCase()}`;
}

/** WCAG relative luminance (0 = black, 1 = white); null when unparseable. */
export function relativeLuminance(
	hex: string | null | undefined,
): number | null {
	const rgb = parseHexColor(hex);
	if (rgb == null) {
		return null;
	}
	const [r, g, b] = rgb.map((v) => {
		const c = v / 255;
		return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
	}) as [number, number, number];
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colors (1 to 21); null when either is unparseable. */
export function contrastRatio(
	a: string | null | undefined,
	b: string | null | undefined,
): number | null {
	const la = relativeLuminance(a);
	const lb = relativeLuminance(b);
	if (la == null || lb == null) {
		return null;
	}
	const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
	return (hi + 0.05) / (lo + 0.05);
}

/**
 * The text color a filled surface (a button, the header) should carry: white
 * or near-black, whichever contrasts more. Null when NEITHER reaches the AA
 * floor — that is the value the editor and the API refuse, since a button
 * painted with it could not carry a readable label.
 */
export function readableTextOn(hex: string | null | undefined): string | null {
	const onWhite = contrastRatio(hex, BRAND_LIGHT_TEXT);
	const onDark = contrastRatio(hex, BRAND_DARK_TEXT);
	if (onWhite == null || onDark == null) {
		return null;
	}
	// White wins whenever it passes: a brand button conventionally carries a
	// white label, and near-black is the fallback for pale fills.
	if (onWhite >= BRAND_MIN_CONTRAST) {
		return BRAND_LIGHT_TEXT;
	}
	if (onDark >= BRAND_MIN_CONTRAST) {
		return BRAND_DARK_TEXT;
	}
	return null;
}

/**
 * The nearest darker shade of `hex` on which white text reaches the AA floor,
 * stepping 1% toward black at a time. Used to seed brand values that were
 * agreed by eye and sit just under the floor. Returns the input when it
 * already passes or cannot be parsed.
 */
export function nearestWhiteReadableFill(hex: string): string {
	const start = normalizeHexColor(hex);
	if (start == null) {
		return hex;
	}
	let candidate = start;
	for (let share = 0.01; share <= 1; share += 0.01) {
		const ratio = contrastRatio(candidate, BRAND_LIGHT_TEXT);
		if (ratio != null && ratio >= BRAND_MIN_CONTRAST) {
			return candidate;
		}
		candidate = mixHex(start, '#000000', share) ?? candidate;
	}
	return candidate;
}

/** `readableTextOn` as a space-separated rgb triplet for Tailwind alpha modifiers. */
export function readableTextRgbOn(hex: string | null | undefined): string {
	const text = readableTextOn(hex) ?? BRAND_LIGHT_TEXT;
	const rgb = parseHexColor(text) ?? [255, 255, 255];
	return rgb.join(' ');
}

/** Mix `hex` toward `toward` by `share` (0–1); null when unparseable. */
export function mixHex(
	hex: string,
	toward: string,
	share: number,
): string | null {
	const a = parseHexColor(hex);
	const b = parseHexColor(toward);
	if (a == null || b == null) {
		return null;
	}
	const s = Math.min(1, Math.max(0, share));
	return rgbToHex([
		a[0] + (b[0] - a[0]) * s,
		a[1] + (b[1] - a[1]) * s,
		a[2] + (b[2] - a[2]) * s,
	]);
}

/**
 * The accent as TEXT (links, active tab) on a given surface. A fill can be
 * dark enough to carry a white label while being too dark to read as text on
 * a navy card, so the text color is derived separately: the accent itself
 * when it already clears AA on the surface, otherwise the accent moved toward
 * white (dark surfaces) or black (light surfaces) in small steps until it
 * does. Returns the accent unchanged when it cannot be parsed.
 */
export function accentTextOn(accent: string, surface: string): string {
	const surfaceLum = relativeLuminance(surface);
	if (surfaceLum == null || parseHexColor(accent) == null) {
		return accent;
	}
	const toward = surfaceLum < 0.5 ? BRAND_LIGHT_TEXT : '#000000';
	let candidate = normalizeHexColor(accent) ?? accent;
	for (let step = 0; step <= 20; step++) {
		const ratio = contrastRatio(candidate, surface);
		if (ratio != null && ratio >= BRAND_MIN_CONTRAST) {
			return candidate;
		}
		const next = mixHex(accent, toward, (step + 1) * 0.05);
		if (next == null) {
			return candidate;
		}
		candidate = next;
	}
	return candidate;
}

/** A darker hover fill for a button painted with `accent`. */
export function accentHover(accent: string): string {
	return mixHex(accent, '#000000', 0.12) ?? accent;
}

export interface BrandPaletteInputModel {
	primaryColor: string | null;
	accentColorLight: string | null;
	accentColorDark: string | null;
}

/** Everything the storefront needs for one mode, fully resolved. */
export interface ResolvedBrandPaletteModel {
	mode: 'light' | 'dark';
	/** Header and bottom nav fill: the primary in light mode, the neutral in dark. */
	header: string;
	/** Text on the header fill, as an rgb triplet for Tailwind alpha modifiers. */
	headerTextRgb: string;
	/** Browser chrome (`theme-color`) — follows the header. */
	themeColor: string;
	/** Button fill. */
	accent: string;
	accentHover: string;
	/** Text on a button fill. */
	accentText: string;
	/** The accent as text on the card surface. */
	accentLinkText: string;
	/** Pre-login welcome and login backdrop, the brand primary in both modes. */
	loginBackground: string;
	neutrals: StorefrontNeutralsModel;
}

/**
 * Resolve the brand inputs for a mode. Missing values fall back to the
 * standard palette; a light accent missing but a dark one present (or the
 * reverse) borrows the other, since one brand color is better than the
 * standard blue next to it.
 */
export function resolveBrandPalette(
	input: Partial<BrandPaletteInputModel>,
	mode: 'light' | 'dark',
): ResolvedBrandPaletteModel {
	const neutrals = STOREFRONT_NEUTRALS[mode];
	const primary =
		normalizeHexColor(input.primaryColor ?? '') ??
		STANDARD_BRAND_PALETTE.primaryColor;
	const light = normalizeHexColor(input.accentColorLight ?? '');
	const dark = normalizeHexColor(input.accentColorDark ?? '');
	const accent =
		mode === 'light'
			? (light ?? dark ?? STANDARD_BRAND_PALETTE.accentColorLight)
			: (dark ?? light ?? STANDARD_BRAND_PALETTE.accentColorDark);
	const header = mode === 'light' ? primary : neutrals.header;
	return {
		mode,
		header,
		headerTextRgb: readableTextRgbOn(header),
		themeColor: header,
		accent,
		accentHover: accentHover(accent),
		accentText: readableTextOn(accent) ?? BRAND_LIGHT_TEXT,
		accentLinkText: accentTextOn(accent, neutrals.surface),
		loginBackground: primary,
		neutrals,
	};
}

/**
 * Why a brand color is refused, or null when it is fine. Shared by the
 * editor (inline message) and the API (400). A blank value is fine: it means
 * "use the standard palette".
 */
export function brandColorProblem(
	value: string | null | undefined,
): string | null {
	if (value == null || value.trim() === '') {
		return null;
	}
	if (!isHexColor(value)) {
		return 'Enter a hex color such as #0058DB.';
	}
	if (readableTextOn(value) == null) {
		return 'Neither white nor near-black text reaches 4.5:1 on this color, so a button painted with it would not be readable. Pick a darker or lighter shade.';
	}
	return null;
}
